import React, { useEffect, useState, useMemo, useCallback } from "react";
import { auth, db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  setDoc,
  doc,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import {
  FaSwimmingPool,
  FaCalendarPlus,
  FaListAlt,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaExclamationTriangle,
  FaDollarSign,
  FaInfoCircle,
  FaDumbbell,
  FaHotTub,
  FaFire,
  FaTree,
  FaGlassCheers,
  FaFutbol,
  FaSpa,
  FaCouch,
  FaLayerGroup,
} from "react-icons/fa";
import { useBuilding } from "../context/BuildingContext";
import { useEffectiveIdentity, useIsReadOnly } from "../context/EffectiveIdentityContext";
import { normalizeAmenity } from "./admin/AmenityConfigTab";
import { formatMoney } from "../utils/format";
import { tolerantId } from "../utils/firestoreHelpers";
import { esPermisoDenegado, leerMiAcceso } from "../utils/consultasEdificio";
import { leerFichaResidente } from "../utils/fichaResidente";
import { useToast } from "../components/Toast";
import { useAppDialog } from "../components/AppDialog";
import useIsDemo from "../hooks/useIsDemo";
import { useIsMobile } from "../hooks/useResponsive";
import {
  AppScreen,
  AppHeader,
  AppCard,
  AppButton,
  Badge,
  AppEmptyState,
  SegmentedControl,
  SectionLabel,
  IconBox,
} from "../components/app";
import { appColors, appType, appSpacing, appRadius, appShadows } from "../utils/appTheme";
import { isDateBlocked, isDateRaffle } from "../utils/amenityRules";
import { useTranslation } from "react-i18next";

// Icono por tipo de espacio (heurística por nombre, solo presentación).
const amenityIcon = (a) => {
  const n = `${a?.name || a?.amenity || ""}`.toLowerCase();
  if (/(pool|pilet|piscina|nat)/.test(n)) return <FaSwimmingPool />;
  if (/(gym|gimnas|fitness|entren)/.test(n)) return <FaDumbbell />;
  if (/(sauna|spa|hidro|jacuzzi|termal)/.test(n)) return <FaHotTub />;
  if (/(masaj|relax|wellness)/.test(n)) return <FaSpa />;
  if (/(parrill|barbaco|bbq|asad|quincho)/.test(n)) return <FaFire />;
  if (/(sum|salón|salon|evento|fiesta|social|cumple)/.test(n)) return <FaGlassCheers />;
  if (/(cancha|fútbol|futbol|tenis|deport|paddle|padel)/.test(n)) return <FaFutbol />;
  if (/(jardín|jardin|parque|terraza|verde|patio)/.test(n)) return <FaTree />;
  if (/(coworking|sala|estar|living|lounge|cine)/.test(n)) return <FaCouch />;
  return <FaLayerGroup />;
};

/**
 * userid canónico, igual que idKey de las reglas (firestore.0d.rules): 102 y
 * "0102" dan "102"; los no numéricos ("demo_resident") quedan igual.
 */
export const idKey = (x) => {
  if (typeof x === "number") return String(x);
  if (typeof x === "string") return /^[0-9]+$/.test(x) ? String(Number(x)) : x;
  return "";
};

/**
 * Id de la anotación a un sorteo (C8): uno por persona, amenity y fecha. Las
 * reglas exigen exactamente este id, así nadie suma chances anotándose dos veces.
 */
export const signupDocId = (amenityId, date, userid) => `${amenityId}_${date}_${idKey(userid)}`;

// Variantes de userid por consulta: × 2 variantes del edificio ([10, "10"]) =
// 28 disyunciones, debajo del tope de 30 de Firestore.
const VARIANTES_USERID_POR_CONSULTA = 14;

/**
 * Reservas o anotaciones a sorteos de un edificio (campo buildingid).
 *
 * El residente vigente lee las de todo el edificio (calendario de
 * disponibilidad, cupos de los sorteos). El ex-ocupante solo lee las SUYAS
 * (reglas 0D, C12) y una consulta que no fija el userid se le niega entera: en
 * ese caso se repite fijando los userid de la cuenta (user_access.userids). Sin
 * uid ("ver como": quien mira es staff del edificio) no hay segundo intento.
 *
 * @param {string} coleccion - reservas | amenity_signups
 * @param {string | number} buildingId - Edificio
 * @param {string | null} uid - uid de la sesión, o null en "ver como"
 * @returns {Promise<Array<Record<string, any> & { id: string }>>}
 */
export async function leerDelEdificioOPropias(coleccion, buildingId, uid) {
  const col = collection(db, coleccion);
  const delEdificio = where("buildingid", "in", tolerantId(buildingId));
  try {
    const snap = await getDocs(query(col, delEdificio));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    if (!uid || !esPermisoDenegado(e)) throw e;
    const acceso = await leerMiAcceso(db, uid);
    const porClave = new Map();
    (Array.isArray(acceso?.userids) ? acceso.userids : [])
      .flatMap((id) => tolerantId(id))
      .forEach((v) => porClave.set(`${typeof v}:${v}`, v));
    const variantes = [...porClave.values()];
    if (variantes.length === 0) throw e;
    const porId = new Map();
    for (let i = 0; i < variantes.length; i += VARIANTES_USERID_POR_CONSULTA) {
      const tanda = variantes.slice(i, i + VARIANTES_USERID_POR_CONSULTA);
      const snap = await getDocs(query(col, delEdificio, where("userid", "in", tanda)));
      snap.docs.forEach((d) => porId.set(d.id, { id: d.id, ...d.data() }));
    }
    return [...porId.values()];
  }
}

const STATUS_LABELS = { active: "Disponible", inactive: "No disponible", maintenance: "En mantenimiento" };
const STATUS_COLORS = { active: "#10b981", inactive: "#6b7280", maintenance: "#f59e0b" };

const Amenities = ({ selectedBuilding: selectedBuildingProp, setSelected, goBack }) => {
  const { t: ta } = useTranslation("amenities");
  const { selectedBuilding: selectedBuildingCtx } = useBuilding();
  const { email: effEmail, userid: effUserid } = useEffectiveIdentity();
  // "Ver como residente": modo solo lectura para el admin. Bloquea todas las acciones.
  const viendoComo = useIsReadOnly();
  // Modo demo: ocultamos botón "Reservar" (write).
  const { isDemo: isDemoUser } = useIsDemo();
  const isMobile = useIsMobile();
  const selectedBuilding = selectedBuildingProp || selectedBuildingCtx;
  // También es de solo lectura el edificio donde la cuenta ya no vive
  // (ex-ocupante): las reglas no le dejan reservar ni anotarse a sorteos.
  const readOnly = viendoComo || !!selectedBuilding?.isReadOnly;
  const { showToast } = useToast();
  const { confirmDialog } = useAppDialog();

  // Vista activa en mobile (solo presentación): "reservar" | "mis"
  const [mobileView, setMobileView] = useState("reservar");

  // Data
  const [amenityConfigs, setAmenityConfigs] = useState([]);
  const [reservas, setReservas] = useState([]); // solo las del usuario (Mis Reservas)
  const [allReservas, setAllReservas] = useState([]); // todas las del edificio (calendario)
  const [allSignups, setAllSignups] = useState([]); // R6+: anotaciones a sorteos del edificio
  const [mySignups, setMySignups] = useState([]); // R6+: anotaciones del usuario
  const [signupSaving, setSignupSaving] = useState(false);
  const [userId, setUserId] = useState(null);
  const [userApartment, setUserApartment] = useState(null);
  const [residentType, setResidentType] = useState("propietario");
  const [loading, setLoading] = useState(true);

  // Form state
  const [selectedAmenityId, setSelectedAmenityId] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTurno, setSelectedTurno] = useState("");
  const [confirmingReserva, setConfirmingReserva] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Calendar state
  const now = new Date();
  const [calendarMonth, setCalendarMonth] = useState(now.getMonth());
  const [calendarYear, setCalendarYear] = useState(now.getFullYear());

  const getRelatedReservationUserIds = useCallback((uid, apartments) => {
    const current = String(uid || "");
    const ids = new Set([current]);
    apartments.forEach((apt) => {
      const participants = [
        apt.owner,
        apt.ownerSecondary,
        apt.tenant,
        apt.tenantSecondary,
      ].filter((x) => x != null && String(x).trim() !== "");
      const isInUnit = participants.some((x) => String(x) === current);
      if (isInUnit) {
        participants.forEach((x) => ids.add(String(x)));
      }
    });
    return ids;
  }, []);

  // ============================================================
  // Fetch data
  // ============================================================
  const fetchData = useCallback(async () => {
    if (!selectedBuilding) return;
    setLoading(true);
    try {
      if (!effEmail) return;

      // Resolve userId (en "ver como", del contexto: quien mira es staff y con
      // C10 no lista users por email).
      const ficha = await leerFichaResidente(db, { email: effEmail, userid: effUserid, viendoComo });
      if (!ficha) return;
      const uid = ficha.userid;
      setUserId(uid);

      // Fetch amenities
      const amenQ = query(
        collection(db, "amenitiesbuilding"),
        where("buildingid", "in", tolerantId(selectedBuilding.buildingid))
      );
      const amenSnap = await getDocs(amenQ);
      const configs = amenSnap.docs
        .map((d) => normalizeAmenity({ id: d.id, ...d.data() }))
        .filter((a) => a.status === "active");
      setAmenityConfigs(configs);

      // Resolve user apartment + ids relacionados (titular/complementario)
      const aptQ = query(
        collection(db, "apartments"),
        where("building", "in", tolerantId(selectedBuilding.buildingid))
      );
      const aptSnap = await getDocs(aptQ);
      const apartments = aptSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      for (const aptDoc of apartments) {
        const apt = aptDoc;
        if (String(apt.owner) === String(uid) || String(apt.ownerSecondary) === String(uid)) {
          setUserApartment({ id: aptDoc.id, ...apt });
          setResidentType("propietario");
          break;
        }
        if (String(apt.tenant) === String(uid) || String(apt.tenantSecondary) === String(uid)) {
          setUserApartment({ id: aptDoc.id, ...apt });
          setResidentType("inquilino");
          break;
        }
      }

      // Reservas del edificio (calendario de disponibilidad); el ex-ocupante,
      // solo las suyas (C12).
      const uidSesion = viendoComo ? null : auth.currentUser?.uid || null;
      const allRes = await leerDelEdificioOPropias("reservas", selectedBuilding.buildingid, uidSesion);
      setAllReservas(allRes);

      // Filtrar solo las del usuario y su unidad (para "Mis Reservas")
      const relatedUserIds = getRelatedReservationUserIds(uid, apartments);
      setReservas(allRes.filter((r) => relatedUserIds.has(String(r.userid))));

      // R6+: anotaciones a sorteos (amenity_signups) del edificio; el
      // ex-ocupante, solo las suyas (C12).
      try {
        const allSignups = await leerDelEdificioOPropias("amenity_signups", selectedBuilding.buildingid, uidSesion);
        setAllSignups(allSignups);
        setMySignups(allSignups.filter((s) => relatedUserIds.has(String(s.userid))));
      } catch (e) {
        console.error("Error cargando anotaciones de sorteo:", e);
      }
    } catch (err) {
      console.error("Error cargando datos:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedBuilding, effEmail, effUserid, viendoComo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ============================================================
  // Selected amenity config
  // ============================================================
  const selectedConfig = useMemo(() => {
    if (!selectedAmenityId) return null;
    return amenityConfigs.find((a) => a.id === selectedAmenityId) || null;
  }, [selectedAmenityId, amenityConfigs]);

  // Get cost for selected turn
  const selectedTurnConfig = useMemo(() => {
    if (!selectedConfig || !selectedTurno) return null;
    return (selectedConfig.turns || []).find((t) => t.name === selectedTurno) || null;
  }, [selectedConfig, selectedTurno]);

  const selectedCost = selectedTurnConfig && selectedConfig?.category === "con_costo"
    ? Number(selectedTurnConfig.cost) || 0
    : 0;

  // ============================================================
  // Calendar computations
  // ============================================================
  const MONTH_NAMES = ta("calendar.months", { returnObjects: true });

  const calendarDays = useMemo(() => {
    const firstDay = new Date(calendarYear, calendarMonth, 1);
    const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
    // Lunes = 0, Domingo = 6 (ajuste para semana empezando en Lunes)
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    const days = [];
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  }, [calendarMonth, calendarYear]);

  // Todas las reservas del edificio para el amenity seleccionado, agrupadas por día
  const reservasByDay = useMemo(() => {
    if (!selectedConfig) return {};
    const amenityName = selectedConfig.name || selectedConfig.amenity;
    const byDay = {};
    allReservas.forEach((r) => {
      if (r.status === "cancelled") return;
      if (r.amenity !== amenityName && r.amenityId !== selectedConfig.id) return;
      const [ry, rm] = (r.date || "").split("-").map(Number);
      if (ry === calendarYear && rm === calendarMonth + 1) {
        const rd = parseInt((r.date || "").split("-")[2], 10);
        if (!byDay[rd]) byDay[rd] = [];
        byDay[rd].push(r);
      }
    });
    return byDay;
  }, [allReservas, selectedConfig, calendarMonth, calendarYear]);

  const getSlotStatus = useCallback((day, turnName) => {
    const dayReservas = (reservasByDay[day] || []).filter((r) => r.turno === turnName && r.status !== "cancelled");
    if (selectedConfig?.exclusive) {
      return dayReservas.length > 0 ? "reserved" : "available";
    }
    const cap = selectedConfig?.maxCapacity || 1;
    return dayReservas.length >= cap ? "reserved" : "available";
  }, [reservasByDay, selectedConfig]);

  const handleCalendarPrev = () => {
    if (calendarMonth === 0) { setCalendarMonth(11); setCalendarYear((y) => y - 1); }
    else setCalendarMonth((m) => m - 1);
  };
  const handleCalendarNext = () => {
    if (calendarMonth === 11) { setCalendarMonth(0); setCalendarYear((y) => y + 1); }
    else setCalendarMonth((m) => m + 1);
  };

  const handleDayClick = (day) => {
    if (!day) return;
    const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    // No permitir días pasados
    const todayObj = new Date(); todayObj.setHours(0, 0, 0, 0);
    const clickedDate = new Date(calendarYear, calendarMonth, day);
    if (clickedDate < todayObj) return;
    // R6+: fecha de SORTEO → seleccionarla para anotarse (no requiere turno ni día
    // disponible; la fecha de sorteo está explícitamente habilitada).
    if (isDateRaffle(selectedConfig, dateStr)) {
      setSelectedDate(dateStr);
      setSelectedTurno("");
      setShowConfirmation(false);
      return;
    }
    // No permitir días no disponibles
    const dayOfWeek = clickedDate.getDay();
    if (!(selectedConfig?.availableDays || []).includes(dayOfWeek)) return;
    // R6: no permitir fechas bloqueadas (feriados, fechas puntuales)
    if (isDateBlocked(selectedConfig, dateStr)) return;
    setSelectedDate(dateStr);
    setShowConfirmation(false);
    // Limpiar turno si el seleccionado ya no está disponible en la nueva fecha
    if (selectedTurno && getSlotStatus(day, selectedTurno) === "reserved") {
      setSelectedTurno("");
    }
  };

  // R6+: sorteo — anotarse a una fecha de sorteo (no reserva; luego la admin sortea).
  const mySignupFor = (amenityName, date) =>
    mySignups.find((s) => s.amenityName === amenityName && s.date === date && s.status !== "anulado");
  const signupCountFor = (amenityName, date) =>
    allSignups.filter((s) => s.amenityName === amenityName && s.date === date && s.status !== "anulado").length;

  const handleRaffleSignup = async () => {
    if (readOnly) { showToast(ta("toast.readOnly")); return; }
    if (!selectedConfig || !selectedDate || !userId) return;
    const amenityName = selectedConfig.name || selectedConfig.amenity;
    const signupId = signupDocId(selectedConfig.id, selectedDate, userId);
    if (mySignupFor(amenityName, selectedDate) || allSignups.some((s) => s.id === signupId)) {
      showToast(ta("toast.alreadySignedUp"), "info");
      return;
    }
    setSignupSaving(true);
    try {
      const payload = {
        buildingid: selectedBuilding.buildingid,
        amenityId: selectedConfig.id,
        amenityName,
        date: selectedDate,
        userid: userId,
        apartmentId: userApartment?.id || "",
        unitNumber: userApartment?.number || userApartment?.name || "",
        residentType,
        status: "anotado",
        createdAt: serverTimestamp(),
      };
      // setDoc con el id determinístico (C8): si ya existe, es un update y las
      // reglas lo niegan (solo el staff resuelve el sorteo).
      await setDoc(doc(db, "amenity_signups", signupId), payload);
      const local = { id: signupId, ...payload };
      setMySignups((prev) => [...prev, local]);
      setAllSignups((prev) => [...prev, local]);
      showToast(ta("toast.signedUpSuccess"), "success");
    } catch (e) {
      console.error("Error anotándose al sorteo:", e);
      showToast(ta("toast.signupError"), "error");
    } finally {
      setSignupSaving(false);
    }
  };

  // ============================================================
  // Helpers
  // ============================================================
  const today = new Date().toISOString().split("T")[0];

  const isPast = (date) => {
    if (!date) return false;
    const [y, m, d] = date.split("-").map(Number);
    const dateLocal = new Date(y, m - 1, d);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return dateLocal < now;
  };

  // Check if cancellation is free
  const isCancellationFree = (reserva) => {
    if (!reserva.date) return false;
    const config = amenityConfigs.find((a) => (a.name || a.amenity) === reserva.amenity);
    const freeDays = config?.rules?.freeCancellationDays || 0;
    if (freeDays === 0) return true;
    const [y, m, d] = reserva.date.split("-").map(Number);
    const reservaDate = new Date(y, m - 1, d);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const diffMs = reservaDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return diffDays >= freeDays;
  };

  // ============================================================
  // Reservation logic
  // ============================================================
  const handleReserva = async () => {
    if (readOnly) { showToast(ta("toast.readOnly")); return; }
    if (!selectedConfig || !selectedDate || !selectedTurno) {
      showToast(ta("toast.completeFields"), "error");
      return;
    }

    // R6+: una fecha de SORTEO no se reserva directo — hay que anotarse al sorteo.
    if (isDateRaffle(selectedConfig, selectedDate)) {
      showToast(ta("toast.raffleDate"), "error");
      return;
    }

    // Validate date not in past
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    const [yyyy, mm, dd] = selectedDate.split("-").map(Number);
    const selectedDateObj = new Date(yyyy, mm - 1, dd);
    if (selectedDateObj < todayDate) {
      showToast(ta("toast.pastDate"), "error");
      return;
    }

    // Validate day is available
    const dow = selectedDateObj.getDay();
    if (!(selectedConfig.availableDays || []).includes(dow)) {
      const dayNames = ta("calendar.dayNamesSundayFirst", { returnObjects: true });
      showToast(ta("toast.notAvailableOnDay", { day: dayNames[dow] }), "error");
      return;
    }

    // R6: Validate date is not blocked (feriados / fechas puntuales)
    if (isDateBlocked(selectedConfig, selectedDate)) {
      showToast(ta("toast.blockedDate"), "error");
      return;
    }

    // Validate min advance days
    const minAdvance = selectedConfig.rules?.minAdvanceDays || 0;
    if (minAdvance > 0) {
      const diffMs = selectedDateObj.getTime() - todayDate.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays < minAdvance) {
        showToast(ta("toast.minAdvance", { days: minAdvance }), "error");
        return;
      }
    }

    // Check max active reservations per unit
    const maxActive = selectedConfig.rules?.maxActiveReservationsPerUnit || 0;
    if (maxActive > 0 && userApartment) {
      const activeReservas = reservas.filter(
        (r) => r.amenity === (selectedConfig.name || selectedConfig.amenity) &&
               r.status !== "cancelled" && !isPast(r.date)
      );
      if (activeReservas.length >= maxActive) {
        showToast(ta("toast.maxActive", { active: activeReservas.length, max: maxActive }), "error");
        return;
      }
    }

    // Check slot availability
    const existingQ = query(
      collection(db, "reservas"),
      where("buildingid", "in", tolerantId(selectedBuilding.buildingid)),
      where("amenity", "==", selectedConfig.name || selectedConfig.amenity),
      where("date", "==", selectedDate),
      where("turno", "==", selectedTurno)
    );
    const existingSnap = await getDocs(existingQ);
    const confirmedSlots = existingSnap.docs.filter(
      (d) => d.data().status !== "cancelled"
    );

    if (selectedConfig.exclusive && confirmedSlots.length > 0) {
      showToast(ta("toast.turnTaken"), "error");
      return;
    }
    if (!selectedConfig.exclusive && selectedConfig.maxCapacity) {
      if (confirmedSlots.length >= selectedConfig.maxCapacity) {
        showToast(ta("toast.capacityReached", { max: selectedConfig.maxCapacity }), "error");
        return;
      }
    }

    // If con_costo, show confirmation
    if (selectedConfig.category === "con_costo" && selectedCost > 0 && !showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    await createReservation();
  };

  const createReservation = async () => {
    if (readOnly) { showToast(ta("toast.readOnly")); return; }
    setConfirmingReserva(true);
    try {
      const amenityName = selectedConfig.name || selectedConfig.amenity;
      const reservaRef = doc(collection(db, "reservas"));

      // Validacion de disponibilidad fuera de la transaccion
      // (tx.get() no soporta Query objects en Firestore Web SDK)
      const existingQ = query(
        collection(db, "reservas"),
        where("buildingid", "in", tolerantId(selectedBuilding.buildingid)),
        where("amenity", "==", amenityName),
        where("date", "==", selectedDate),
        where("turno", "==", selectedTurno)
      );
      const existingSnap = await getDocs(existingQ);
      const confirmedSlots = existingSnap.docs.filter((d) => d.data().status !== "cancelled");

      if (selectedConfig.exclusive && confirmedSlots.length > 0) {
        throw new Error(ta("error.slotTakenByOther"));
      }
      if (!selectedConfig.exclusive && selectedConfig.maxCapacity && confirmedSlots.length >= selectedConfig.maxCapacity) {
        throw new Error(ta("error.capacityReached"));
      }

      await runTransaction(db, async (tx) => {
        tx.set(reservaRef, {
          userid: userId,
          buildingid: selectedBuilding.buildingid,
          amenity: amenityName,
          amenityId: selectedConfig.id,
          date: selectedDate,
          turno: selectedTurno,
          apartmentId: userApartment?.id || "",
          unitNumber: userApartment?.number || userApartment?.name || "",
          residentType: residentType,
          status: "confirmed",
          createdBy: userId,
          createdAt: serverTimestamp(),
          cost: (selectedConfig.category === "con_costo" && selectedCost > 0) ? selectedCost : 0,
          currency: selectedConfig.currency || "UYU",
        });

        if (selectedConfig.category === "con_costo" && selectedCost > 0) {
          const [y, m, d] = selectedDate.split("-").map(Number);
          const dateObj = new Date(y, m - 1, d);
          const chargeRef = doc(collection(db, "amenitycharges"));
          tx.set(chargeRef, {
            reservaId: reservaRef.id,
            amenityId: selectedConfig.id,
            userid: userId,
            apartmentId: userApartment?.id || "",
            buildingid: selectedBuilding.buildingid,
            amenity: amenityName,
            turno: selectedTurno,
            date: selectedDate,
            cost: selectedCost,
            currency: selectedConfig.currency || "UYU",
            concept: `Reserva de ${amenityName} - ${selectedDate} - Turno ${selectedTurno}`,
            residentType: residentType,
            status: "pending",
            month: dateObj.getMonth() + 1,
            year: dateObj.getFullYear(),
            targetFundId: selectedConfig.targetFundId || "reserva",
            createdAt: serverTimestamp(),
          });
        }
      });

      // La tarea de limpieza (amenity con requiresCleaning) la crea el servidor
      // con un trigger sobre la reserva nueva (C11): el residente no lee el
      // personal del edificio (employeebuilding: sueldos y horarios) ni escribe
      // employee_tasks.

      showToast(
        selectedCost > 0
          ? ta("toast.reservationCreatedWithCharge", {
              amount: formatMoney(selectedCost, selectedConfig.currency || "UYU"),
            })
          : ta("toast.reservationCreated"),
        "success"
      );

      // Reset form
      setSelectedAmenityId("");
      setSelectedDate("");
      setSelectedTurno("");
      setShowConfirmation(false);
      fetchData();
    } catch (err) {
      console.error("Error creando reserva:", err);
      showToast(ta("toast.reservationError", { message: err.message }), "error");
    } finally {
      setConfirmingReserva(false);
    }
  };

  // ============================================================
  // Cancellation
  // ============================================================
  const handleCancel = async (reserva) => {
    if (readOnly) { showToast(ta("toast.readOnly")); return; }
    const isFree = isCancellationFree(reserva);
    const config = amenityConfigs.find((a) => (a.name || a.amenity) === reserva.amenity);
    const freeDays = config?.rules?.freeCancellationDays || 0;
    const msg = isFree
      ? ta("cancelDialog.messageFree")
      : ta("cancelDialog.messageNotFree", { count: freeDays });
    const okCancel = await confirmDialog(msg, { title: ta("cancelDialog.title"), danger: true, confirmLabel: ta("cancelDialog.confirmLabel") });
    if (!okCancel) return;

    try {
      await updateDoc(doc(db, "reservas", reserva.id), {
        status: "cancelled",
        cancelledAt: serverTimestamp(),
        cancelledBy: "resident",
      });

      // Reverse charge if free cancellation
      if (isFree) {
        const chargeQ = query(
          collection(db, "amenitycharges"),
          where("reservaId", "==", reserva.id)
        );
        const chargeSnap = await getDocs(chargeQ);
        for (const chargeDoc of chargeSnap.docs) {
          if (chargeDoc.data().status === "pending") {
            await updateDoc(doc(db, "amenitycharges", chargeDoc.id), {
              status: "reversed",
              reversedAt: serverTimestamp(),
            });
          }
        }
      }

      // La tarea de limpieza de la reserva la maneja el servidor (C11): el
      // residente ya no lee ni cancela employee_tasks.

      showToast(ta("toast.cancelSuccess"), "success");
      fetchData();
    } catch (err) {
      console.error("Error cancelando reserva:", err);
      showToast(ta("toast.cancelError", { message: err.message }), "error");
    }
  };

  // ============================================================
  // Reservas split
  // ============================================================
  const cancelledByAdmin = reservas.filter(
    (r) => r.status === "cancelled" && r.cancelledBy === "admin" && !isPast(r.date)
  );
  const activeReservas = reservas.filter((r) => r.status === "confirmed" || r.status === undefined);
  const pastReservas = activeReservas.filter((r) => isPast(r.date));
  const futureReservas = activeReservas.filter((r) => !isPast(r.date));

  // ============================================================
  // Render
  // ============================================================
  if (loading) {
    if (isMobile) {
      return (
        <AppScreen
          header={
            <AppHeader
              title={ta("titleMobile")}
              subtitle={selectedBuilding?.name || ""}
              onBack={goBack}
            />
          }
        >
          <p style={{ ...appType.bodySm, textAlign: "center", padding: 40 }}>
            {ta("loadingMobile")}
          </p>
        </AppScreen>
      );
    }
    return (
      <div style={styles.container}>
        <p style={{ textAlign: "center", color: "#6b7280", padding: 40 }}>{ta("loading")}</p>
      </div>
    );
  }

  // ─── Versión mobile: estilo app "Radial" (screen 07) ────────────────────────
  if (isMobile) {
    const requiresReservation =
      selectedConfig &&
      (selectedConfig.category === "con_costo" || selectedConfig.requiresReservation);
    const isFreeAccess =
      selectedConfig &&
      selectedConfig.category === "sin_costo" &&
      !selectedConfig.requiresReservation;
    const selectedDay = selectedDate ? parseInt(selectedDate.split("-")[2], 10) : null;
    const reserveLabel = confirmingReserva
      ? ta("reserveButton.booking")
      : showConfirmation
        ? ta("reserveButton.confirm")
        : selectedConfig && selectedDate && selectedTurno
          ? ta("reserveButton.bookNamed", { name: selectedConfig.name || selectedConfig.amenity, turno: selectedTurno })
          : ta("reserveButton.book");

    return (
      <AppScreen
        header={
          <AppHeader
            title={ta("titleMobile")}
            subtitle={selectedBuilding?.name || ""}
            onBack={goBack}
          />
        }
      >
        <SegmentedControl
          options={[
            { value: "reservar", label: ta("tabs.book") },
            { value: "mis", label: ta("tabs.myReservations") },
          ]}
          value={mobileView}
          onChange={setMobileView}
          style={{ marginBottom: appSpacing.section }}
        />

        {/* Aviso de cancelaciones por administración */}
        {cancelledByAdmin.length > 0 && (
          <AppCard
            accent="blue"
            style={{ marginBottom: appSpacing.section, borderLeftColor: appColors.red }}
          >
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <FaExclamationTriangle style={{ color: appColors.red, flexShrink: 0, marginTop: 2 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ ...appType.body, color: appColors.red }}>
                  {ta("adminCancelled.titleMobile")}
                </div>
                {cancelledByAdmin.map((r, i) => (
                  <div key={i} style={{ ...appType.bodySm, marginTop: 4, wordBreak: "break-word" }}>
                    {r.amenity} — {r.date} — {r.turno}
                    {r.cancelReason && (
                      <div style={{ ...appType.caption, marginTop: 2 }}>{ta("adminCancelled.reason", { reason: r.cancelReason })}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </AppCard>
        )}

        {mobileView === "reservar" ? (
          <>
            {/* Paso 1: elegí el espacio */}
            <SectionLabel style={{ marginBottom: 12 }}>{ta("step1")}</SectionLabel>
            {amenityConfigs.length === 0 ? (
              <AppEmptyState
                icon={<FaSwimmingPool />}
                title={ta("emptyAmenities.title")}
                description={ta("emptyAmenities.description")}
              />
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: appSpacing.gridGap,
                  marginBottom: appSpacing.section,
                }}
              >
                {amenityConfigs.map((a) => {
                  const active = selectedAmenityId === a.id;
                  const tag =
                    a.category === "con_costo"
                      ? ta("tag.withCost")
                      : !a.requiresReservation
                        ? ta("tag.freeAccess")
                        : ta("tag.noCost");
                  const tagColor =
                    a.category === "con_costo"
                      ? appColors.blue
                      : !a.requiresReservation
                        ? appColors.green
                        : appColors.ink3;
                  // Subtítulo: capacidad si es compartido, si no, "Reservable".
                  const capacityLabel =
                    !a.exclusive && a.maxCapacity > 0
                      ? ta("capacityLabel.people", { n: a.maxCapacity })
                      : a.requiresReservation
                        ? ta("capacityLabel.bookable")
                        : ta("capacityLabel.freeAccess");
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => {
                        setSelectedAmenityId(a.id);
                        setSelectedTurno("");
                        setShowConfirmation(false);
                      }}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 10,
                        textAlign: "left",
                        minWidth: 0,
                        padding: 16,
                        borderRadius: appRadius.tile,
                        background: appColors.surface,
                        border: `2px solid ${active ? appColors.blue : appColors.line2}`,
                        boxShadow: appShadows.soft,
                        cursor: "pointer",
                      }}
                    >
                      <IconBox
                        icon={amenityIcon(a)}
                        size={44}
                        bg={active ? appColors.blue : appColors.surface2}
                        color={active ? appColors.onBlue : appColors.blue}
                      />
                      <span
                        style={{
                          ...appType.tileName,
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {a.name || a.amenity}
                      </span>
                      <span
                        style={{
                          ...appType.caption,
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {capacityLabel}
                      </span>
                      {/* Línea de estado inferior coloreada */}
                      <span
                        style={{
                          ...appType.statLabel,
                          color: tagColor,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          marginTop: 2,
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: tagColor,
                            flexShrink: 0,
                          }}
                        />
                        {tag}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Info del espacio seleccionado */}
            {selectedConfig?.description && (
              <AppCard accent="blue" style={{ marginBottom: appSpacing.section }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <FaInfoCircle style={{ color: appColors.blue, flexShrink: 0, marginTop: 2 }} />
                  <span style={{ ...appType.body, wordBreak: "break-word" }}>
                    {selectedConfig.description}
                  </span>
                </div>
              </AppCard>
            )}

            {/* Acceso libre: no requiere reserva */}
            {isFreeAccess && (
              <AppCard accent="blue" style={{ marginBottom: appSpacing.section, borderLeftColor: appColors.green }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <FaInfoCircle style={{ color: appColors.green, flexShrink: 0, marginTop: 2 }} />
                  <span style={{ ...appType.body }}>
                    {ta("freeAccessNote")}
                  </span>
                </div>
              </AppCard>
            )}

            {/* Paso 2: horarios */}
            {requiresReservation && (
              <>
                <SectionLabel style={{ marginBottom: 12 }}>
                  {ta("step2", { name: (selectedConfig?.name || selectedConfig?.amenity || "").toUpperCase() })}
                </SectionLabel>

                {/* Calendario de disponibilidad */}
                <AppCard style={{ marginBottom: appSpacing.section }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 12,
                    }}
                  >
                    <button
                      type="button"
                      onClick={handleCalendarPrev}
                      aria-label={ta("calendar.prevMonth")}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        border: `1px solid ${appColors.line}`,
                        background: appColors.surface,
                        color: appColors.ink2,
                        fontSize: 18,
                        cursor: "pointer",
                      }}
                    >
                      ‹
                    </button>
                    <span style={{ ...appType.body, fontSize: 15 }}>
                      {MONTH_NAMES[calendarMonth]} {calendarYear}
                    </span>
                    <button
                      type="button"
                      onClick={handleCalendarNext}
                      aria-label={ta("calendar.nextMonth")}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        border: `1px solid ${appColors.line}`,
                        background: appColors.surface,
                        color: appColors.ink2,
                        fontSize: 18,
                        cursor: "pointer",
                      }}
                    >
                      ›
                    </button>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
                    {ta("calendar.weekdaysShort", { returnObjects: true }).map((d) => (
                      <div
                        key={d}
                        style={{
                          ...appType.statLabel,
                          fontSize: 10,
                          textAlign: "center",
                          padding: "4px 0",
                        }}
                      >
                        {d}
                      </div>
                    ))}
                    {calendarDays.map((day, idx) => {
                      if (day === null) return <div key={`empty-${idx}`} />;
                      const dateObj = new Date(calendarYear, calendarMonth, day);
                      const todayObj = new Date();
                      todayObj.setHours(0, 0, 0, 0);
                      const isPastDay = dateObj < todayObj;
                      const dayOfWeek = dateObj.getDay();
                      const isAvailableDay = (selectedConfig.availableDays || []).includes(dayOfWeek);
                      const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                      const isBlockedDay = isDateBlocked(selectedConfig, dateStr); // R6
                      const isRaffleDay = isDateRaffle(selectedConfig, dateStr);   // R6+
                      const isDisabled = isPastDay || (isBlockedDay && !isRaffleDay) || (!isAvailableDay && !isRaffleDay);
                      const isSelected = selectedDate === dateStr;
                      const turns = selectedConfig.turns || [];
                      const raffleCount = isRaffleDay ? signupCountFor(selectedConfig.name || selectedConfig.amenity, dateStr) : 0;
                      const iAmSignedUp = isRaffleDay && !!mySignupFor(selectedConfig.name || selectedConfig.amenity, dateStr);
                      return (
                        <div
                          key={day}
                          onClick={() => !isDisabled && handleDayClick(day)}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "6px 2px",
                            borderRadius: 10,
                            minHeight: 42,
                            background: isSelected ? appColors.blue : isRaffleDay ? "#FEF3C7" : "transparent",
                            border: isRaffleDay && !isSelected ? "1px solid #FCD34D" : undefined,
                            opacity: isDisabled ? 0.4 : 1,
                            cursor: isDisabled ? "default" : "pointer",
                          }}
                        >
                          <span
                            style={{
                              ...appType.body,
                              fontSize: 14,
                              color: isSelected
                                ? appColors.onBlue
                                : isRaffleDay
                                  ? "#b45309"
                                  : isDisabled
                                    ? appColors.ink3
                                    : appColors.ink,
                            }}
                          >
                            {day}
                          </span>
                          {isRaffleDay ? (
                            <span style={{ fontSize: 9, color: isSelected ? appColors.onBlue : iAmSignedUp ? "#15803d" : "#b45309", marginTop: 1 }}>
                              {iAmSignedUp ? ta("raffle.signedUp") : raffleCount > 0 ? ta("raffle.countTag", { n: raffleCount }) : ta("raffle.tag")}
                            </span>
                          ) : !isDisabled && (
                            <div style={{ display: "flex", gap: 3, marginTop: 2 }}>
                              {turns.map((t) => {
                                const status = getSlotStatus(day, t.name);
                                return (
                                  <span
                                    key={t.name}
                                    style={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: "50%",
                                      background:
                                        status === "available" ? appColors.green : appColors.red,
                                    }}
                                    title={ta("slot.title", { turn: t.name, status: status === "available" ? ta("slot.available") : ta("slot.reserved") })}
                                  />
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 16,
                      flexWrap: "wrap",
                      marginTop: 12,
                      paddingTop: 10,
                      borderTop: `1px solid ${appColors.line2}`,
                    }}
                  >
                    {[
                      { c: appColors.green, l: ta("slot.available") },
                      { c: appColors.red, l: ta("slot.reserved") },
                      { c: appColors.ink3, l: ta("slot.notAvailable") },
                    ].map((it) => (
                      <div key={it.l} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: it.c }} />
                        <span style={{ ...appType.caption }}>{it.l}</span>
                      </div>
                    ))}
                  </div>
                </AppCard>

                {/* R6+: fecha de SORTEO → anotarse (no se elige turno ni se reserva) */}
                {isDateRaffle(selectedConfig, selectedDate) ? (
                  <AppCard accent="blue" style={{ borderLeftColor: appColors.amber, marginBottom: appSpacing.section }}>
                    <div style={{ ...appType.statLabel, color: appColors.amber, marginBottom: 6 }}>{ta("raffle.dateLabel")}</div>
                    <div style={{ ...appType.bodySm, color: appColors.ink3, marginBottom: appSpacing.md, lineHeight: 1.5 }}>
                      {ta("raffle.descriptionMobile")}
                    </div>
                    <div style={{ ...appType.bodySm, color: appColors.ink2, marginBottom: appSpacing.md }}>
                      {ta("raffle.signedUpCount", { n: signupCountFor(selectedConfig.name || selectedConfig.amenity, selectedDate) })}
                    </div>
                    {mySignupFor(selectedConfig.name || selectedConfig.amenity, selectedDate) ? (
                      <div style={{ ...appType.body, color: appColors.green }}>{ta("raffle.youAreSignedUp")}</div>
                    ) : !isDemoUser && !readOnly ? (
                      <AppButton block onClick={handleRaffleSignup} disabled={signupSaving}>
                        {signupSaving ? ta("raffle.signingUp") : ta("raffle.signUp")}
                      </AppButton>
                    ) : (
                      <span style={{ ...appType.bodySm, color: appColors.ink3 }}>{readOnly ? ta("readOnlyShort") : ta("demoShort")}</span>
                    )}
                  </AppCard>
                ) : (
                <>
                {/* Turnos: grilla 3-col que envuelve (nunca scroll horizontal) */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: appSpacing.gridGap,
                    marginBottom: appSpacing.section,
                  }}
                >
                  {(selectedConfig.turns || []).map((t) => {
                    const isTurnReserved = selectedDay
                      ? getSlotStatus(selectedDay, t.name) === "reserved"
                      : false;
                    const active = selectedTurno === t.name;
                    return (
                      <button
                        key={t.name}
                        type="button"
                        disabled={isTurnReserved}
                        onClick={() => {
                          if (isTurnReserved) return;
                          setSelectedTurno(t.name);
                          setShowConfirmation(false);
                        }}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 3,
                          minWidth: 0,
                          padding: "13px 8px",
                          borderRadius: appRadius.pill,
                          textAlign: "center",
                          background: active ? appColors.blue : appColors.surface,
                          border: `2px solid ${active ? appColors.blue : appColors.line2}`,
                          color: active ? appColors.onBlue : appColors.ink,
                          opacity: isTurnReserved ? 0.4 : 1,
                          cursor: isTurnReserved ? "not-allowed" : "pointer",
                        }}
                      >
                        <span
                          style={{
                            ...appType.body,
                            fontSize: 14,
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: isTurnReserved
                              ? appColors.ink3
                              : active
                                ? appColors.onBlue
                                : appColors.ink,
                          }}
                        >
                          {isTurnReserved ? ta("turn.occupied") : t.startTime}
                        </span>
                        {!isTurnReserved && (
                          <span
                            style={{
                              ...appType.caption,
                              fontSize: 10,
                              maxWidth: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: active ? appColors.onBlue : appColors.ink3,
                            }}
                          >
                            {selectedConfig.category === "con_costo" && t.cost > 0
                              ? formatMoney(Number(t.cost), selectedConfig.currency || "UYU")
                              : t.name}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Capacidad (amenities compartidos) */}
                {!selectedConfig.exclusive && selectedConfig.maxCapacity > 0 && (
                  <div style={{ ...appType.bodySm, marginBottom: appSpacing.md }}>
                    {ta("capacityPerTurn", { n: selectedConfig.maxCapacity })}
                  </div>
                )}

                {/* Confirmación de costo */}
                {showConfirmation && selectedCost > 0 && (
                  <AppCard accent="blue" style={{ marginBottom: appSpacing.section }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <FaDollarSign style={{ color: appColors.blue, flexShrink: 0, marginTop: 2 }} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ ...appType.body }}>
                          {ta("cost.willChargeMobile")}
                        </div>
                        {/* Caja de valor resaltada (cream FRÍO) */}
                        <div
                          style={{
                            marginTop: 10,
                            padding: "12px 14px",
                            borderRadius: appRadius.sm,
                            background: appColors.cream,
                          }}
                        >
                          <div style={{ ...appType.statLabel, color: appColors.ink3 }}>{ta("cost.amountLabel")}</div>
                          <div
                            style={{
                              ...appType.body,
                              fontSize: 22,
                              letterSpacing: "-.01em",
                              fontVariantNumeric: "tabular-nums",
                              marginTop: 2,
                            }}
                          >
                            {formatMoney(selectedCost, selectedConfig.currency || "UYU")}
                          </div>
                          {(selectedConfig.currency === "UI" || selectedConfig.currency === "UR") && (
                            <div style={{ ...appType.caption, color: appColors.amber, marginTop: 4 }}>
                              {ta("cost.convertNote")}
                            </div>
                          )}
                        </div>
                        <div style={{ ...appType.caption, marginTop: 8 }}>
                          {ta("cost.occupantType", { type: residentType === "propietario" ? ta("occupantType.owner") : ta("occupantType.tenant") })}
                          {userApartment &&
                            ta("cost.unitSuffix", { unit: userApartment.name || userApartment.number || userApartment.id })}
                        </div>
                      </div>
                    </div>
                  </AppCard>
                )}

                {/* CTA (oculto en modo demo y en "ver como residente"/solo lectura) */}
                {!isDemoUser && !readOnly ? (
                  <AppButton
                    block
                    onClick={handleReserva}
                    disabled={confirmingReserva}
                  >
                    {reserveLabel}
                  </AppButton>
                ) : (
                  <AppCard accent="blue" style={{ borderLeftColor: appColors.amber }}>
                    <span style={{ ...appType.body }}>
                      {readOnly
                        ? ta("readOnlyCard.readOnly")
                        : ta("readOnlyCard.demoMobile")}
                    </span>
                  </AppCard>
                )}
                </>
                )}
              </>
            )}
          </>
        ) : (
          <>
            {/* Mis reservas */}
            {futureReservas.length === 0 && pastReservas.length === 0 ? (
              <AppEmptyState
                icon={<FaSwimmingPool />}
                title={ta("myReservations.emptyTitle")}
                description={ta("myReservations.emptyDescription")}
              />
            ) : (
              <>
                {futureReservas.length > 0 && (
                  <>
                    <SectionLabel style={{ marginBottom: 12 }}>{ta("myReservations.upcoming")}</SectionLabel>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                        marginBottom: pastReservas.length > 0 ? appSpacing.section : 0,
                      }}
                    >
                      {futureReservas.map((r) => (
                        <AppCard key={r.id} accent="blue">
                          {/* Cabecera: icono + nombre + estado */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "space-between",
                              gap: 12,
                            }}
                          >
                            <div style={{ display: "flex", gap: 12, minWidth: 0, flex: 1 }}>
                              <IconBox icon={amenityIcon(r)} size={40} />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ ...appType.body, fontSize: 14, overflowWrap: "anywhere" }}>
                                  {r.amenity}
                                </div>
                                <div style={{ ...appType.bodySm, marginTop: 2 }}>{r.turno}</div>
                              </div>
                            </div>
                            <Badge tone="green" dot>
                              {ta("status.confirmed")}
                            </Badge>
                          </div>

                          {/* Caja de valor resaltada (cream FRÍO) para el importe */}
                          {r.cost > 0 && (
                            <div
                              style={{
                                marginTop: 12,
                                padding: "10px 12px",
                                borderRadius: appRadius.sm,
                                background: appColors.cream,
                              }}
                            >
                              <div style={{ ...appType.statLabel, color: appColors.ink3 }}>{ta("myReservations.amountLabel")}</div>
                              <div
                                style={{
                                  ...appType.body,
                                  fontSize: 18,
                                  fontVariantNumeric: "tabular-nums",
                                  marginTop: 2,
                                }}
                              >
                                {formatMoney(r.cost, r.currency || "UYU")}
                              </div>
                            </div>
                          )}

                          {/* Footer estructurado: FECHA / UNIDAD */}
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 12,
                              marginTop: 12,
                              paddingTop: 12,
                              borderTop: `1px solid ${appColors.line2}`,
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <div style={{ ...appType.statLabel, color: appColors.ink3 }}>{ta("myReservations.dateLabel")}</div>
                              <div style={{ ...appType.bodySm, color: appColors.ink, marginTop: 2 }}>
                                {r.date}
                              </div>
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ ...appType.statLabel, color: appColors.ink3 }}>{ta("myReservations.unitLabel")}</div>
                              <div
                                style={{
                                  ...appType.bodySm,
                                  color: appColors.ink,
                                  marginTop: 2,
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {r.unitNumber || "—"}
                              </div>
                            </div>
                          </div>

                          {/* Cancelar (full-width, patrón app) — oculto en solo lectura */}
                          {!readOnly && (
                            <button
                              type="button"
                              onClick={() => handleCancel(r)}
                              aria-label={ta("cancelReservation")}
                              title={ta("cancelReservation")}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 8,
                                width: "100%",
                                marginTop: 12,
                                padding: "11px 12px",
                                borderRadius: appRadius.button,
                                border: `1px solid ${appColors.line}`,
                                background: appColors.surface,
                                color: appColors.red,
                                fontFamily: appType.fontFamily,
                                fontSize: 13.5,
                                fontWeight: 400,
                                cursor: "pointer",
                              }}
                            >
                              <FaTimesCircle style={{ fontSize: 12 }} />
                              {ta("cancelReservation")}
                            </button>
                          )}
                        </AppCard>
                      ))}
                    </div>
                  </>
                )}

                {pastReservas.length > 0 && (
                  <>
                    <SectionLabel style={{ marginBottom: 12 }}>{ta("myReservations.finished")}</SectionLabel>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {pastReservas.map((r) => (
                        <AppCard
                          key={r.id || r.date + r.turno}
                          accent="blue"
                          style={{ borderLeftColor: appColors.ink3 }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "space-between",
                              gap: 12,
                            }}
                          >
                            <div style={{ display: "flex", gap: 12, minWidth: 0, flex: 1 }}>
                              <IconBox icon={amenityIcon(r)} size={40} color={appColors.ink3} />
                              <div style={{ minWidth: 0 }}>
                                <div
                                  style={{
                                    ...appType.body,
                                    fontSize: 14,
                                    color: appColors.ink2,
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  {r.amenity}
                                </div>
                                <div style={{ ...appType.bodySm, marginTop: 2 }}>
                                  {[r.date, r.turno].filter(Boolean).join(" · ")}
                                  {r.cost > 0
                                    ? ` · ${formatMoney(r.cost, r.currency || "UYU")}`
                                    : ""}
                                </div>
                              </div>
                            </div>
                            <Badge tone="ink3" dot>
                              {ta("status.finished")}
                            </Badge>
                          </div>
                        </AppCard>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </AppScreen>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerIcon}><FaSwimmingPool /></div>
        <div style={styles.headerInfo}>
          <h1 style={styles.title}>{ta("title")}</h1>
          <p style={styles.subtitle}>{ta("subtitle")}</p>
        </div>
      </div>

      {/* Admin cancellation banner */}
      {cancelledByAdmin.length > 0 && (
        <div style={styles.cancelBanner}>
          <FaExclamationTriangle style={{ color: "#dc2626", flexShrink: 0 }} />
          <div>
            <strong>{ta("adminCancelled.titleDesktop")}</strong>
            {cancelledByAdmin.map((r, i) => (
              <div key={i} style={{ fontSize: 13, marginTop: 4 }}>
                {r.amenity} — {r.date} — {r.turno}
                {r.cancelReason && (
                  <div style={{ fontSize: 12, color: "#991b1b", marginTop: 2 }}>
                    {ta("adminCancelled.reason", { reason: r.cancelReason })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={styles.grid}>
        {/* ========== NEW RESERVATION PANEL ========== */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <FaCalendarPlus style={styles.cardIcon} />
            <h3 style={styles.cardTitle}>{ta("newReservation")}</h3>
          </div>

          {/* Amenity selector */}
          <select
            value={selectedAmenityId}
            onChange={(e) => {
              setSelectedAmenityId(e.target.value);
              setSelectedTurno("");
              setShowConfirmation(false);
            }}
            style={styles.select}
          >
            <option value="">{ta("selectPlaceholder")}</option>
            {amenityConfigs.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name || a.amenity}
                {a.category === "con_costo" ? ta("optionSuffix.withCost") : ""}
                {a.category === "sin_costo" && !a.requiresReservation ? ta("optionSuffix.freeAccess") : ""}
              </option>
            ))}
          </select>

          {/* Selected amenity info */}
          {selectedConfig && (
            <>
              {/* Info card */}
              {selectedConfig.description && (
                <div style={styles.infoBanner}>
                  <FaInfoCircle style={{ color: "#0080ff", flexShrink: 0 }} />
                  <span>{selectedConfig.description}</span>
                </div>
              )}

              {/* Access libre message */}
              {selectedConfig.category === "sin_costo" && !selectedConfig.requiresReservation && (
                <div style={styles.infoBanner}>
                  <FaInfoCircle style={{ color: "#10b981", flexShrink: 0 }} />
                  <span>{ta("freeAccessNote")}</span>
                </div>
              )}

              {/* Reservation form (only if requires reservation) */}
              {(selectedConfig.category === "con_costo" || selectedConfig.requiresReservation) && (
                <>
                  {/* Calendario visual de disponibilidad */}
                  <div style={calStyles.calendarContainer}>
                    {/* Navegación del mes */}
                    <div style={calStyles.calendarNav}>
                      <button
                        onClick={handleCalendarPrev}
                        style={calStyles.navBtn}
                        aria-label={ta("calendar.prevMonth")}
                      >
                        ‹
                      </button>
                      <span style={calStyles.monthLabel}>
                        {MONTH_NAMES[calendarMonth]} {calendarYear}
                      </span>
                      <button
                        onClick={handleCalendarNext}
                        style={calStyles.navBtn}
                        aria-label={ta("calendar.nextMonth")}
                      >
                        ›
                      </button>
                    </div>

                    {/* Headers de días */}
                    <div style={calStyles.calendarGrid}>
                      {ta("calendar.weekdaysShort", { returnObjects: true }).map((d) => (
                        <div key={d} style={calStyles.dayHeader}>{d}</div>
                      ))}

                      {/* Celdas de días */}
                      {calendarDays.map((day, idx) => {
                        if (day === null) return <div key={`empty-${idx}`} />;
                        const dateObj = new Date(calendarYear, calendarMonth, day);
                        const todayObj = new Date(); todayObj.setHours(0, 0, 0, 0);
                        const isPastDay = dateObj < todayObj;
                        const dayOfWeek = dateObj.getDay();
                        const isAvailableDay = (selectedConfig.availableDays || []).includes(dayOfWeek);
                        const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                        const isBlockedDay = isDateBlocked(selectedConfig, dateStr); // R6
                        const isRaffleDay = isDateRaffle(selectedConfig, dateStr);   // R6+
                        const isDisabled = isPastDay || (isBlockedDay && !isRaffleDay) || (!isAvailableDay && !isRaffleDay);
                        const isSelected = selectedDate === dateStr;
                        const turns = selectedConfig.turns || [];
                        const raffleCount = isRaffleDay ? signupCountFor(selectedConfig.name || selectedConfig.amenity, dateStr) : 0;
                        const iAmSignedUp = isRaffleDay && !!mySignupFor(selectedConfig.name || selectedConfig.amenity, dateStr);

                        return (
                          <div
                            key={day}
                            onClick={() => !isDisabled && handleDayClick(day)}
                            style={{
                              ...calStyles.dayCell,
                              ...(isDisabled ? calStyles.dayCellDisabled : {}),
                              ...(isRaffleDay && !isSelected ? { background: "#FEF3C7", border: "1px solid #FCD34D" } : {}),
                              ...(isSelected ? calStyles.dayCellSelected : {}),
                              cursor: isDisabled ? "default" : "pointer",
                            }}
                          >
                            <span style={{
                              ...calStyles.dayNumber,
                              ...(isSelected ? { color: "#ffffff" } : {}),
                              ...(isRaffleDay && !isSelected ? { color: "#b45309" } : {}),
                              ...(isDisabled && !isSelected ? { color: "#d1d5db" } : {}),
                            }}>
                              {day}
                            </span>
                            {isRaffleDay ? (
                              <span style={{ fontSize: 9, color: isSelected ? "#ffffff" : iAmSignedUp ? "#15803d" : "#b45309" }}>
                                {iAmSignedUp ? ta("raffle.signedUp") : raffleCount > 0 ? ta("raffle.countTag", { n: raffleCount }) : ta("raffle.tag")}
                              </span>
                            ) : !isDisabled && (
                              <div style={calStyles.dotsRow}>
                                {turns.map((t) => {
                                  const status = getSlotStatus(day, t.name);
                                  return (
                                    <span
                                      key={t.name}
                                      style={{
                                        ...calStyles.dot,
                                        backgroundColor: status === "available" ? "#10b981" : "#ef4444",
                                      }}
                                      title={ta("slot.title", { turn: t.name, status: status === "available" ? ta("slot.available") : ta("slot.reserved") })}
                                    />
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Leyenda */}
                    <div style={calStyles.legend}>
                      <div style={calStyles.legendItem}>
                        <span style={{ ...calStyles.legendDot, backgroundColor: "#10b981" }} />
                        <span>{ta("slot.available")}</span>
                      </div>
                      <div style={calStyles.legendItem}>
                        <span style={{ ...calStyles.legendDot, backgroundColor: "#ef4444" }} />
                        <span>{ta("slot.reserved")}</span>
                      </div>
                      <div style={calStyles.legendItem}>
                        <span style={{ ...calStyles.legendDot, backgroundColor: "#d1d5db" }} />
                        <span>{ta("slot.notAvailable")}</span>
                      </div>
                    </div>
                  </div>

                  {/* R6+: fecha de SORTEO → anotarse (no se reserva directo) */}
                  {isDateRaffle(selectedConfig, selectedDate) ? (
                    <AppCard accent="blue" style={{ borderLeftColor: appColors.amber, marginBottom: appSpacing.section }}>
                      <div style={{ ...appType.statLabel, color: appColors.amber, marginBottom: 6 }}>{ta("raffle.dateLabel")}</div>
                      <div style={{ ...appType.bodySm, color: appColors.ink3, marginBottom: appSpacing.md, lineHeight: 1.5 }}>
                        {ta("raffle.descriptionDesktop")}
                      </div>
                      <div style={{ ...appType.bodySm, color: appColors.ink2, marginBottom: appSpacing.md }}>
                        {ta("raffle.signedUpCount", { n: signupCountFor(selectedConfig.name || selectedConfig.amenity, selectedDate) })}
                      </div>
                      {mySignupFor(selectedConfig.name || selectedConfig.amenity, selectedDate) ? (
                        <div style={{ ...appType.body, color: appColors.green }}>{ta("raffle.youAreSignedUp")}</div>
                      ) : !isDemoUser && !readOnly ? (
                        <AppButton block onClick={handleRaffleSignup} disabled={signupSaving}>
                          {signupSaving ? ta("raffle.signingUp") : ta("raffle.signUp")}
                        </AppButton>
                      ) : (
                        <span style={{ ...appType.bodySm, color: appColors.ink3 }}>{readOnly ? ta("readOnlyShort") : ta("demoShort")}</span>
                      )}
                    </AppCard>
                  ) : (
                  <>
                  {/* Dynamic turns */}
                  <div style={styles.turnosContainer}>
                    {(selectedConfig.turns || []).map((t) => {
                      const selectedDay = selectedDate ? parseInt(selectedDate.split("-")[2], 10) : null;
                      const isTurnReserved = selectedDay ? getSlotStatus(selectedDay, t.name) === "reserved" : false;
                      return (
                        <label
                          key={t.name}
                          style={{
                            ...styles.turnoLabel,
                            opacity: isTurnReserved ? 0.4 : 1,
                            cursor: isTurnReserved ? "not-allowed" : "pointer",
                          }}
                        >
                          <input
                            type="radio"
                            name="turno"
                            value={t.name}
                            checked={selectedTurno === t.name}
                            disabled={isTurnReserved}
                            onChange={(e) => { setSelectedTurno(e.target.value); setShowConfirmation(false); }}
                            style={styles.radio}
                          />
                          <span style={styles.turnoOption}>
                            <FaClock style={styles.turnoIcon} />
                            {ta("turn.labelDesktop", { name: t.name, start: t.startTime, end: t.endTime })}
                            {selectedConfig.category === "con_costo" && t.cost > 0 && (
                              <span style={styles.turnoCost}>{formatMoney(Number(t.cost), selectedConfig.currency || "UYU")}</span>
                            )}
                            {isTurnReserved && (
                              <span style={{ marginLeft: 8, fontSize: 12, color: "#ef4444" }}>{ta("turn.reserved")}</span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>

                  {/* Capacity info for shared amenities */}
                  {!selectedConfig.exclusive && selectedConfig.maxCapacity > 0 && (
                    <div style={styles.capacityHint}>
                      {ta("capacityPerTurn", { n: selectedConfig.maxCapacity })}
                    </div>
                  )}

                  {/* Cost confirmation */}
                  {showConfirmation && selectedCost > 0 && (
                    <div style={styles.confirmBanner}>
                      <FaDollarSign style={{ color: "#0080ff" }} />
                      <div>
                        <strong>{ta("cost.willChargeDesktop", { amount: formatMoney(selectedCost, selectedConfig.currency || "UYU") })}</strong>
                        {(selectedConfig.currency === "UI" || selectedConfig.currency === "UR") && (
                          <div style={{ fontSize: 12, color: "#f59e0b", marginTop: 2 }}>
                            {ta("cost.convertNote")}
                          </div>
                        )}
                        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                          {ta("cost.occupantType", { type: residentType === "propietario" ? ta("occupantType.owner") : ta("occupantType.tenant") })}
                          {userApartment && ta("cost.unitSuffix", { unit: userApartment.name || userApartment.number || userApartment.id })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Reserve button (oculto en modo demo y en "ver como residente"/solo lectura) */}
                  {!isDemoUser && !readOnly && (
                    <button
                      onClick={handleReserva}
                      style={styles.reservarBtn}
                      disabled={confirmingReserva}
                    >
                      {confirmingReserva
                        ? ta("reserveButton.booking")
                        : showConfirmation
                          ? ta("reserveButton.confirm")
                          : ta("reserveButton.book")
                      }
                    </button>
                  )}
                  {(isDemoUser || readOnly) && (
                    <div style={{ padding: 12, background: "#fef3c7", color: "#92400e", borderRadius: 6, fontSize: 14 }}>
                      {readOnly
                        ? ta("readOnlyCard.readOnly")
                        : ta("readOnlyCard.demoDesktop")}
                    </div>
                  )}
                  </>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* ========== MY RESERVATIONS PANEL ========== */}
        <div style={styles.cardReservas}>
          <div style={styles.cardHeader}>
            <FaListAlt style={styles.cardIcon} />
            <h3 style={styles.cardTitle}>{ta("myReservations.title")}</h3>
          </div>

          {futureReservas.length === 0 && pastReservas.length === 0 ? (
            <div style={styles.emptyReservas}>
              <FaSwimmingPool style={styles.emptyIcon} />
              <p style={styles.emptyText}>{ta("myReservations.emptyTextDesktop")}</p>
            </div>
          ) : (
            <ul style={styles.reservasList}>
              {/* Future reservas */}
              {futureReservas.map((r) => (
                <li key={r.id} style={{ ...styles.reservaItem, borderLeftColor: "#0080ff" }}>
                  <div style={styles.reservaContent}>
                    <strong style={styles.reservaAmenity}>{r.amenity}</strong>
                    <span style={styles.reservaDate}>
                      {r.date} - {r.turno}
                      {r.cost > 0 && (
                        <span style={{ marginLeft: 8, color: "#f59e0b", fontWeight: 400, fontSize: 12 }}>
                          {formatMoney(r.cost, r.currency || "UYU")}
                        </span>
                      )}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ ...styles.reservaStatus, backgroundColor: "#d1fae5", color: "#065f46" }}>
                      <FaCheckCircle /> {ta("status.confirmed")}
                    </span>
                    {!readOnly && (
                      <button
                        style={styles.cancelReservaBtn}
                        onClick={() => handleCancel(r)}
                        title={ta("cancelReservation")}
                      >
                        <FaTimesCircle />
                      </button>
                    )}
                  </div>
                </li>
              ))}
              {/* Past reservas */}
              {pastReservas.map((r) => (
                <li key={r.id || r.date + r.turno} style={{ ...styles.reservaItem, borderLeftColor: "#9ca3af" }}>
                  <div style={styles.reservaContent}>
                    <strong style={styles.reservaAmenity}>{r.amenity}</strong>
                    <span style={styles.reservaDate}>
                      {r.date} - {r.turno}
                      {r.cost > 0 && (
                        <span style={{ marginLeft: 8, color: "#6b7280", fontWeight: 400, fontSize: 12 }}>
                          {formatMoney(r.cost, r.currency || "UYU")}
                        </span>
                      )}
                    </span>
                  </div>
                  <span style={{ ...styles.reservaStatus, backgroundColor: "#f3f4f6", color: "#6b7280" }}>
                    <FaCheckCircle /> {ta("status.finished")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Styles
// ============================================================
const styles = {
  container: {
    padding: 24,
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    background: "white", borderRadius: 12, padding: "16px 20px", marginBottom: 20,
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)", border: "1px solid #e5e7eb",
    display: "flex", alignItems: "center", gap: 14,
  },
  headerIcon: {
    width: 42, height: 42, borderRadius: 10, backgroundColor: "#0080ff", color: "white",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
  },
  headerInfo: { flex: 1 },
  title: { fontSize: 20, fontWeight: 400, margin: 0, color: "#0080ff" },
  subtitle: { fontSize: 13, color: "#6b7280", margin: "2px 0 0 0", fontWeight: 400 },
  cancelBanner: {
    display: "flex", gap: 12, alignItems: "flex-start", padding: "14px 20px",
    borderRadius: 12, backgroundColor: "#fef2f2", border: "1px solid #fecaca",
    marginBottom: 20, fontSize: 14, color: "#991b1b",
  },
  grid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: 24,
  },
  card: {
    background: "white", borderRadius: 12, padding: 24,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid #e5e7eb",
  },
  cardReservas: {
    background: "white", borderRadius: 12, padding: 24,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid #e5e7eb",
  },
  cardHeader: { display: "flex", alignItems: "center", gap: 10, marginBottom: 20 },
  cardIcon: { color: "#0080ff", fontSize: 20 },
  cardTitle: { fontSize: 18, fontWeight: 400, margin: 0, color: "#1a1a1a" },
  select: {
    padding: 12, borderRadius: 10, border: "1px solid #e5e7eb", marginBottom: 16,
    width: "100%", fontSize: 14, fontFamily: "inherit", color: "#1a1a1a", backgroundColor: "#f8f9fa",
  },
  input: {
    padding: 12, borderRadius: 10, border: "1px solid #e5e7eb", marginBottom: 16,
    width: "100%", fontSize: 14, fontFamily: "inherit", color: "#1a1a1a", backgroundColor: "#f8f9fa",
  },
  infoBanner: {
    display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 16px",
    borderRadius: 10, backgroundColor: "#f0f9ff", border: "1px solid #bfdbfe",
    marginBottom: 16, fontSize: 13, color: "#1e40af",
  },
  daysHint: {
    fontSize: 12, color: "#6b7280", marginBottom: 12, fontStyle: "italic",
  },
  capacityHint: {
    fontSize: 12, color: "#6b7280", marginBottom: 12, padding: "6px 12px",
    backgroundColor: "#f9fafb", borderRadius: 6,
  },
  confirmBanner: {
    display: "flex", gap: 12, alignItems: "flex-start", padding: "14px 16px",
    borderRadius: 10, backgroundColor: "#eff6ff", border: "1px solid #bfdbfe",
    marginBottom: 16, fontSize: 14, color: "#1a1a1a",
  },
  turnosContainer: { marginBottom: 20, display: "flex", flexDirection: "column", gap: 10 },
  turnoLabel: { display: "flex", alignItems: "center", gap: 10, cursor: "pointer" },
  radio: { accentColor: "#0080ff" },
  turnoOption: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#4b5563" },
  turnoIcon: { color: "#0080ff", fontSize: 14 },
  turnoCost: {
    marginLeft: 8, padding: "2px 8px", borderRadius: 6,
    backgroundColor: "#e8f4ff", color: "#0080ff", fontSize: 12, fontWeight: 400,
  },
  reservarBtn: {
    padding: "14px 20px", backgroundColor: "#0080ff", color: "white",
    border: "none", borderRadius: 10, width: "100%", fontSize: 15,
    fontWeight: 400, cursor: "pointer", transition: "background-color 0.2s ease",
    fontFamily: "inherit",
  },
  emptyReservas: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: 40, textAlign: "center",
  },
  emptyIcon: { fontSize: 40, color: "#0080ff", opacity: 0.5, marginBottom: 12 },
  emptyText: { color: "#6b7280", fontSize: 14, margin: 0 },
  reservasList: {
    listStyle: "none", paddingLeft: 0, margin: 0,
    display: "flex", flexDirection: "column", gap: 10,
  },
  reservaItem: {
    padding: 14, borderRadius: 10, backgroundColor: "#f8f9fa",
    border: "1px solid #e5e7eb", borderLeft: "4px solid #0080ff",
    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
  },
  reservaContent: { display: "flex", flexDirection: "column", gap: 4 },
  reservaAmenity: { fontSize: 14, fontWeight: 400, color: "#1a1a1a" },
  reservaDate: { fontSize: 13, color: "#6b7280" },
  reservaStatus: {
    padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 400,
    display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap",
  },
  cancelReservaBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 28, height: 28, borderRadius: 6, border: "1px solid #ef4444",
    backgroundColor: "white", color: "#ef4444", cursor: "pointer", fontSize: 14,
  },
};

// Estilos del calendario visual
const calStyles = {
  calendarContainer: {
    background: "#ffffff",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)",
    marginBottom: 16,
  },
  calendarNav: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  navBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: "none",
    background: "transparent",
    color: "#6b7280",
    fontSize: 20,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.15s ease",
  },
  monthLabel: {
    fontSize: 16,
    fontWeight: 400,
    color: "#1a1a1a",
  },
  calendarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 4,
  },
  dayHeader: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "#6b7280",
    textAlign: "center",
    padding: "4px 0",
    fontWeight: 400,
  },
  dayCell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px 2px",
    borderRadius: 8,
    minHeight: 44,
    transition: "background-color 0.15s ease",
  },
  dayCellDisabled: {
    opacity: 0.4,
  },
  dayCellSelected: {
    backgroundColor: "#0080ff",
  },
  dayNumber: {
    fontSize: 14,
    color: "#1a1a1a",
    fontWeight: 400,
  },
  dotsRow: {
    display: "flex",
    gap: 3,
    marginTop: 2,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
  },
  legend: {
    display: "flex",
    gap: 16,
    marginTop: 12,
    paddingTop: 8,
    borderTop: "1px solid #f3f4f6",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    color: "#6b7280",
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    display: "inline-block",
  },
};

export default Amenities;
